import React from 'react'
import { Col, Container, Form, Row, Stack } from 'react-bootstrap'
import { MultiScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useZocksQuota } from '../../../components/ScopedBlock/hooks'
import PrizeBadge from '../../../components/ScopedBlock/PrizeBadge.svg?url'
import { useGetAccountQuery, useUpdateAccountMutation } from '../../../store/accountUserSettings/accountUserSettings'
import './Settings.scss'
import { LinkButton } from '../../../components/Button/LinkButton'

export const MeetingTrimmingSettings: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)
  const { data: account, isFetching: isAccountFetching } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const [updateAccount] = useUpdateAccountMutation()
  const { hasQuota } = useZocksQuota()
  const restrictedSessionTrimmingSettingAvailable = hasQuota('room.restrictedSessionTrimming.value')

  const restrictedSessionTrimming = account?.data.effectiveConfig.restrictedSessionTrimming ?? false
  const restrictedSessionTrimmingEnterpriseControlled = account?.data.enterpriseConfig.restrictedSessionTrimming != null

  const handleRestrictedSessionTrimming = (checked: boolean) => {
    if (!account) {
      return
    }
    const accountConfig = { ...account.data.config, restrictedSessionTrimming: checked }
    updateAccount({
      accountId: accountId,
      payload: {
        config: accountConfig,
      },
    })
  }

  return (
    <>
      <Container className='d-flex flex-column align-items-start zocks-gap-2'>
        <Row className='p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
          <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
            <Stack direction='horizontal' gap={3}>
              <div className='d-flex align-self-center gap-3 align-items-center'>
                <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                <span className='zocks-text-gray-900'>
                  By default anyone can trim the meetings they created, you can restrict meeting trimming to the Owner and Admin role.
                </span>
              </div>
              {!restrictedSessionTrimmingSettingAvailable && (
                <MultiScopedBlock
                  scopedBlocks={[
                    {
                      scope: { scope: 'account:compliance_config_update', superScope: [] },
                      missingScopeAction: MissingScopeAction.DISABLE,
                    },
                    {
                      scope: { scope: 'account:update', superScope: ['account:admin'] },
                      missingScopeAction: MissingScopeAction.DISABLE,
                    },
                  ]}
                >
                  <LinkButton to='/account/plan' variant='primary' className='d-flex p-1 p-2 align-items-center text-nowrap'>
                    <img alt='Upgrade' src={PrizeBadge} />
                    Get feature
                  </LinkButton>
                </MultiScopedBlock>
              )}
            </Stack>
          </Col>
        </Row>
        {restrictedSessionTrimmingEnterpriseControlled ? (
          <Row className='p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
            <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
              <div className='d-flex align-self-center gap-3 align-items-center'>
                <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                <span className='zocks-text-gray-900'>The restriction on meeting session trimming is managed at the enterprise level.</span>
              </div>
            </Col>
          </Row>
        ) : null}
        <Row className='gap-3 p-0 align-items-center'>
          <Col xs='auto' className='p-0'>
            <div className='paragraph-medium'>Disallow users with User role to trim meetings</div>
          </Col>
          <Col className='p-0'>
            <MultiScopedBlock
              scopedBlocks={[
                {
                  scope: { scope: 'account:compliance_config_update', superScope: [] },
                  missingScopeAction: MissingScopeAction.DISABLE,
                },
                {
                  scope: { scope: 'account:update', superScope: ['account:admin'] },
                  missingScopeAction: MissingScopeAction.DISABLE,
                },
              ]}
            >
              <Form.Switch
                disabled={isAccountFetching || !restrictedSessionTrimmingSettingAvailable || restrictedSessionTrimmingEnterpriseControlled}
                checked={restrictedSessionTrimming}
                onClick={(e) => handleRestrictedSessionTrimming(e.currentTarget.checked)}
              />
            </MultiScopedBlock>
          </Col>
        </Row>
      </Container>
    </>
  )
}
